import { create } from 'zustand';
import { Transaction, ColumnMapping } from '../types';
import { storage } from '../utils/storage';

interface TransactionState {
  transactions: Transaction[];
  mappings: Record<string, ColumnMapping>;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  setTransactions: (transactions: Transaction[]) => void;
  addTransaction: (transaction: Transaction) => void;
  addTransactions: (transactions: Transaction[]) => void;
  updateTransaction: (id: string, transaction: Partial<Transaction>) => void;
  deleteTransaction: (id: string) => void;
  setMappings: (mappings: Record<string, ColumnMapping>) => void;
  addMapping: (name: string, mapping: ColumnMapping) => void;
  saveMapping: (name: string, mapping: ColumnMapping) => void;
  deleteMapping: (name: string) => void;
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  
  // Storage operations
  loadFromStorage: () => Promise<void>;
  saveToStorage: () => Promise<void>;
  clearStorage: () => Promise<void>;
  exportData: () => Promise<string>;
  importData: (json: string) => Promise<void>;
}

export const useTransactionStore = create<TransactionState>((set, get) => ({
  transactions: [],
  mappings: {},
  isLoading: false,
  error: null,

  setTransactions: (transactions) => set({ transactions }),
  addTransaction: (transaction) => 
    set((state) => ({ transactions: [...state.transactions, transaction] })),
  addTransactions: (transactions) =>
    set((state) => ({
      transactions: [...state.transactions, ...transactions],
    })),
  updateTransaction: (id, transaction) =>
    set((state) => ({
      transactions: state.transactions.map((t) =>
        t.id === id ? { ...t, ...transaction } : t
      ),
    })),
  deleteTransaction: (id) =>
    set((state) => ({
      transactions: state.transactions.filter((t) => t.id !== id),
    })),
  setMappings: (mappings) => set({ mappings }),
  addMapping: (name, mapping) =>
    set((state) => ({ mappings: { ...state.mappings, [name]: mapping } })),
  saveMapping: (name, mapping) =>
    set((state) => ({
      mappings: { ...state.mappings, [name]: mapping },
    })),
  deleteMapping: (name) =>
    set((state) => {
      const { [name]: _, ...rest } = state.mappings;
      return { mappings: rest };
    }),
  setLoading: (isLoading) => set({ isLoading }),
  setError: (error) => set({ error }),

  loadFromStorage: async () => {
    set({ isLoading: true, error: null });
    try {
      const [transactions, mappings] = await Promise.all([
        storage.getTransactions(),
        storage.getMappings(),
      ]);
      set({ transactions, mappings });
    } catch (error) {
      set({ error: 'Failed to load data from storage' });
    } finally {
      set({ isLoading: false });
    }
  },

  saveToStorage: async () => {
    set({ isLoading: true, error: null });
    try {
      const { transactions, mappings } = get();
      await Promise.all([
        storage.saveTransactions(transactions),
        Object.entries(mappings).map(([name, mapping]) =>
          storage.saveMapping(name, mapping)
        ),
      ]);
    } catch (error) {
      set({ error: 'Failed to save data to storage' });
    } finally {
      set({ isLoading: false });
    }
  },

  clearStorage: async () => {
    set({ isLoading: true, error: null });
    try {
      await Promise.all([
        storage.clearTransactions(),
        storage.clearMappings(),
      ]);
      set({ transactions: [], mappings: {} });
    } catch (error) {
      set({ error: 'Failed to clear storage' });
    } finally {
      set({ isLoading: false });
    }
  },

  exportData: async () => {
    set({ isLoading: true, error: null });
    try {
      return await storage.exportData();
    } catch (error) {
      set({ error: 'Failed to export data' });
      return '';
    } finally {
      set({ isLoading: false });
    }
  },

  importData: async (json) => {
    set({ isLoading: true, error: null });
    try {
      await storage.importData(json);
      await get().loadFromStorage();
    } catch (error) {
      set({ error: 'Failed to import data' });
    } finally {
      set({ isLoading: false });
    }
  },
})); 